/* Lastoya theme — scroll animations, header state, mobile menu, hero parallax, FAQ, scent lens */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Scroll lock (shared with cart-drawer.js via window.__scrollLocks) ── */
  window.__scrollLocks = window.__scrollLocks || 0;
  const lockScroll = () => {
    window.__scrollLocks += 1;
    document.documentElement.style.overflow = 'hidden';
  };
  const unlockScroll = () => {
    window.__scrollLocks = Math.max(0, window.__scrollLocks - 1);
    if (window.__scrollLocks === 0) document.documentElement.style.overflow = '';
  };

  /* ── Fade-up + image reveal (once) ── */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;
        if (el.hasAttribute('data-fade-up')) el.classList.add('is-visible');
        if (el.hasAttribute('data-reveal-img')) el.classList.add('is-revealed');
        if (el.hasAttribute('data-word-stagger')) el.classList.add('is-visible');
        revealObserver.unobserve(el);
      }
    },
    { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
  );
  document
    .querySelectorAll('[data-fade-up], [data-reveal-img], [data-word-stagger]')
    .forEach((el) => revealObserver.observe(el));

  /* ── Header: transparent over hero → solid past 48px (index only) ── */
  const header = document.getElementById('site-header');
  if (header && !header.classList.contains('header-solid')) {
    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 48);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ── Mobile menu ── */
  const menu = document.getElementById('mobile-menu');
  const menuToggle = document.querySelector('[data-menu-toggle]');
  if (menu && menuToggle) {
    const iconOpen = menuToggle.querySelector('[data-menu-icon-open]');
    const iconClose = menuToggle.querySelector('[data-menu-icon-close]');
    let menuOpen = false;

    const setMenu = (open) => {
      if (open === menuOpen) return;
      menuOpen = open;
      menu.classList.toggle('is-open', open);
      menuToggle.setAttribute('aria-expanded', String(open));
      menuToggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      iconOpen.classList.toggle('hidden', open);
      iconClose.classList.toggle('hidden', !open);
      if (open) lockScroll();
      else unlockScroll();
    };

    menuToggle.addEventListener('click', () => setMenu(!menuOpen));
    menu.addEventListener('click', (e) => {
      if (e.target.closest('a')) setMenu(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menuOpen) setMenu(false);
    });
  }

  /* ── Parallax: hero bg + [data-parallax] images ── */
  const heroParallax = document.querySelector('[data-hero-parallax]');
  const parallaxImgs = Array.from(document.querySelectorAll('[data-parallax]'))
    .map((el) => ({ el, img: el.querySelector('img') }))
    .filter((p) => p.img);
  if (!reducedMotion && (heroParallax || parallaxImgs.length)) {
    let ticking = false;
    const update = () => {
      ticking = false;
      if (heroParallax) {
        const hero = heroParallax.closest('section') || heroParallax.parentElement;
        const progress = Math.min(Math.max(window.scrollY / hero.offsetHeight, 0), 1);
        heroParallax.style.transform = `translate3d(0, ${progress * 6}%, 0)`;
      }
      const vh = window.innerHeight;
      for (const { el, img } of parallaxImgs) {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) continue;
        const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        const y = Math.max(-1, Math.min(1, progress)) * -6;
        img.style.transform = `translate3d(0, ${y}%, 0) scale(1.15)`;
      }
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  /* ── FAQ accordion ── */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-faq-toggle]');
    if (!toggle) return;
    const item = toggle.closest('.faq-item');
    const open = !item.classList.contains('is-open');
    item.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
  });

  /* ── Scent lens (collection grid sidebar) ── */
  const lens = document.getElementById('scent-lens');
  const grid = document.querySelector('[data-product-grid]');
  if (lens && grid) {
    const swap = lens.querySelector('[data-lens-swap]');
    const nameEl = lens.querySelector('[data-lens-name]');
    const notesEl = lens.querySelector('[data-lens-notes]');
    const progressEl = lens.querySelector('.lens-progress');
    const cards = Array.from(grid.querySelectorAll('[data-product-id]'));
    const INTERVAL = 5000;
    let activeIdx = -1;
    let timer = null;

    const render = (card) => {
      const notes = (card.dataset.lensNotes || '').split('|').filter(Boolean);
      nameEl.textContent = card.dataset.lensName || '';
      notesEl.textContent = '';
      notes.forEach((note, i) => {
        const p = document.createElement('p');
        p.className = 'lens-note text-sm text-ink-dim leading-snug';
        p.style.setProperty('--note-delay', `${i * 60}ms`);
        p.textContent = note;
        notesEl.appendChild(p);
      });
    };

    const activate = (idx, animate) => {
      if (idx === activeIdx || !cards[idx]) return;
      activeIdx = idx;
      const apply = () => {
        render(cards[idx]);
        swap.classList.remove('is-swapping');
      };
      if (animate && !reducedMotion) {
        swap.classList.add('is-swapping');
        window.setTimeout(apply, 600);
      } else {
        apply();
      }
      if (progressEl && !reducedMotion) {
        progressEl.style.transition = 'none';
        progressEl.style.transform = 'scaleX(0)';
        void progressEl.offsetWidth;
        progressEl.style.transition = `transform ${INTERVAL}ms linear`;
        progressEl.style.transform = 'scaleX(1)';
      }
    };

    const next = () => {
      if (cards.length < 2) return;
      let idx;
      do {
        idx = Math.floor(Math.random() * cards.length);
      } while (idx === activeIdx);
      activate(idx, true);
    };

    const start = () => {
      if (timer !== null || reducedMotion || cards.length < 2) return;
      timer = window.setInterval(next, INTERVAL);
    };
    const stop = () => {
      window.clearInterval(timer);
      timer = null;
    };

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else start();
    });

    if (cards.length) activate(Math.floor(Math.random() * cards.length), false);
    start();
  }
})();
